import React, { useMemo, useState } from 'react';
import type {
  BusinessInfo,
  PurchaseOrder,
  PurchaseOrderStatus,
  PurchasingInvoiceDefaults,
  Supplier,
  SupplierInvoice,
  InventoryItem,
} from '../types';
import PlusIcon from '../components/icons/PlusIcon';
import EditIcon from '../components/icons/EditIcon';
import TrashIcon from '../components/icons/TrashIcon';
import SupplierModal from '../components/purchasing/SupplierModal';
import PurchaseOrderBuilder from '../components/purchasing/PurchaseOrderBuilder';
import ReceivePreviewModal from '../components/purchasing/ReceivePreviewModal';
import { computePoTotals } from '../lib/nzGst';
import { generatePurchaseOrderPdf } from '../lib/purchaseOrderPdf';
import {
  hasBlockingIssues,
  validatePoPrint,
} from '../lib/purchasingInvoiceValidation';

interface PurchasingPageProps {
  suppliers: Supplier[];
  purchaseOrders: PurchaseOrder[];
  supplierInvoices: SupplierInvoice[];
  inventoryItems: InventoryItem[];
  businessInfo?: BusinessInfo | null;
  purchasingDefaults?: PurchasingInvoiceDefaults;
  onSaveSupplier: (supplier: Supplier) => void | Promise<void>;
  onDeleteSupplier: (supplierId: number) => void | Promise<void>;
  onSavePurchaseOrder: (po: PurchaseOrder) => void | Promise<void>;
  onNavigateToInvoices?: (purchaseOrderId?: string) => void;
}

const statusColors: Record<PurchaseOrderStatus, string> = {
  draft: 'bg-gray-200 text-gray-800',
  ordered: 'bg-blue-200 text-blue-800',
  received: 'bg-green-200 text-green-800',
  cancelled: 'bg-red-200 text-red-800',
};

const PurchasingPage: React.FC<PurchasingPageProps> = ({
  suppliers,
  purchaseOrders,
  supplierInvoices,
  inventoryItems,
  businessInfo,
  purchasingDefaults,
  onSaveSupplier,
  onDeleteSupplier,
  onSavePurchaseOrder,
  onNavigateToInvoices,
}) => {
  const [activeTab, setActiveTab] = useState<'orders' | 'suppliers'>('orders');
  const [statusFilter, setStatusFilter] = useState<PurchaseOrderStatus | 'all'>('all');
  const [isSupplierModalOpen, setIsSupplierModalOpen] = useState(false);
  const [isBuilderOpen, setIsBuilderOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);
  const [editingPO, setEditingPO] = useState<PurchaseOrder | null>(null);
  const [builderSupplierId, setBuilderSupplierId] = useState<number | undefined>();
  const [receiveTarget, setReceiveTarget] = useState<PurchaseOrder | null>(null);
  const [receiveLoading, setReceiveLoading] = useState(false);

  const supplierMap = useMemo(() => new Map(suppliers.map((s) => [s.id, s])), [suppliers]);
  const invoiceByPoId = useMemo(() => {
    const map = new Map<string, SupplierInvoice>();
    for (const inv of supplierInvoices) {
      if (inv.purchaseOrderId) map.set(inv.purchaseOrderId, inv);
    }
    return map;
  }, [supplierInvoices]);

  const filteredOrders = useMemo(() => {
    if (statusFilter === 'all') return purchaseOrders;
    return purchaseOrders.filter((po) => po.status === statusFilter);
  }, [purchaseOrders, statusFilter]);

  const lastOrderBySupplier = useMemo(() => {
    const map = new Map<number, PurchaseOrder>();
    for (const po of purchaseOrders) {
      const prev = map.get(po.supplierId);
      if (!prev || po.dateCreated > prev.dateCreated) {
        map.set(po.supplierId, po);
      }
    }
    return map;
  }, [purchaseOrders]);

  const handleOpenSupplierModal = (supplier: Supplier | null = null) => {
    setEditingSupplier(supplier);
    setIsSupplierModalOpen(true);
  };

  const handleOpenBuilder = (po: PurchaseOrder | null = null, supplierId?: number) => {
    setEditingPO(po);
    setBuilderSupplierId(supplierId);
    setIsBuilderOpen(true);
  };

  const handleMarkReceived = (po: PurchaseOrder) => {
    if (po.status === 'received') return;
    setReceiveTarget(po);
  };

  const confirmReceive = async () => {
    if (!receiveTarget) return;
    setReceiveLoading(true);
    try {
      await onSavePurchaseOrder({
        ...receiveTarget,
        status: 'received',
        dateReceived: Date.now(),
      });
      setReceiveTarget(null);
      if (!invoiceByPoId.has(receiveTarget.id) && onNavigateToInvoices) {
        const go = window.confirm(
          'Order received. Record the supplier tax invoice in Invoices now?',
        );
        if (go) onNavigateToInvoices(receiveTarget.id);
      }
    } finally {
      setReceiveLoading(false);
    }
  };

  const handleDownloadPo = (po: PurchaseOrder) => {
    const supplier = supplierMap.get(po.supplierId);
    if (!supplier || !businessInfo) {
      alert('Complete Business Information in Settings before printing.');
      return;
    }
    const issues = validatePoPrint(po, businessInfo);
    if (hasBlockingIssues(issues)) {
      alert(issues.filter((i) => i.level === 'error').map((i) => i.message).join('\n'));
      return;
    }
    generatePurchaseOrderPdf({ po, supplier, businessInfo, defaults: purchasingDefaults });
  };

  return (
    <div className="flex h-full flex-col">
      <header className="z-10 flex-shrink-0 bg-white shadow-md">
        <div className="mx-auto flex h-16 max-w-full items-center justify-between px-4 sm:px-6 lg:px-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Purchasing & Suppliers</h1>
            <p className="text-sm text-gray-500">Order from suppliers — record their invoices in Invoices</p>
          </div>
          <button
            type="button"
            onClick={() =>
              activeTab === 'orders' ? handleOpenBuilder() : handleOpenSupplierModal()
            }
            className="flex items-center rounded-lg bg-pink-500 px-4 py-2 font-bold text-white transition-colors hover:bg-pink-600"
          >
            <PlusIcon className="mr-2 h-5 w-5" />
            {activeTab === 'orders' ? 'New purchase order' : 'Add supplier'}
          </button>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto p-4 sm:p-6">
        <nav className="-mb-px mb-6 flex space-x-6 border-b border-gray-200" aria-label="Tabs">
          <button
            type="button"
            onClick={() => setActiveTab('orders')}
            className={`whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium ${
              activeTab === 'orders'
                ? 'border-pink-500 text-pink-600'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
            }`}
          >
            Purchase orders ({purchaseOrders.length})
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('suppliers')}
            className={`whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium ${
              activeTab === 'suppliers'
                ? 'border-pink-500 text-pink-600'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
            }`}
          >
            Suppliers ({suppliers.length})
          </button>
        </nav>

        {activeTab === 'orders' ? (
          <>
            <div className="mb-4 flex flex-wrap gap-2">
              {(['all', 'draft', 'ordered', 'received', 'cancelled'] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatusFilter(s)}
                  className={`rounded-full px-3 py-1 text-sm font-medium capitalize ${
                    statusFilter === s
                      ? 'bg-pink-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>

            {filteredOrders.length === 0 ? (
              <div className="rounded-xl border-2 border-dashed border-pink-200 bg-pink-50/50 px-6 py-12 text-center">
                <p className="text-lg font-medium text-gray-800">No purchase orders yet</p>
                <button
                  type="button"
                  onClick={() => handleOpenBuilder()}
                  className="mt-4 rounded-lg bg-pink-600 px-4 py-2 text-sm font-semibold text-white"
                >
                  Create your first order
                </button>
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {filteredOrders.map((po) => {
                  const supplier = supplierMap.get(po.supplierId);
                  const gstMode = po.gstMode ?? purchasingDefaults?.defaultGstMode ?? 'inclusive';
                  const totals = computePoTotals(po.items, gstMode);
                  const linkedInv = invoiceByPoId.get(po.id);
                  return (
                    <article
                      key={po.id}
                      className="flex flex-col rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
                    >
                      <div className="mb-2 flex items-start justify-between gap-2">
                        <div>
                          <p className="font-semibold text-gray-900">{po.id}</p>
                          <p className="text-sm text-gray-600">{supplier?.name ?? 'Unknown'}</p>
                        </div>
                        <span
                          className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold capitalize ${statusColors[po.status]}`}
                        >
                          {po.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500">
                        {po.items.length} lines · {new Date(po.dateCreated).toLocaleDateString()}
                      </p>
                      <p className="mt-1 text-lg font-bold text-pink-700">
                        ${totals.totalIncGst.toFixed(2)}{' '}
                        <span className="text-xs font-normal text-gray-500">inc. GST</span>
                      </p>
                      {linkedInv ? (
                        <p className="mt-1 text-xs text-green-700">
                          Invoice recorded: {linkedInv.invoiceNumber}
                        </p>
                      ) : po.status === 'received' ? (
                        <p className="mt-1 text-xs text-amber-700">Supplier invoice not recorded</p>
                      ) : null}
                      <div className="mt-4 flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => handleOpenBuilder(po)}
                          className="flex items-center rounded-md border px-2 py-1 text-sm"
                        >
                          <EditIcon className="mr-1 h-4 w-4" /> Edit
                        </button>
                        {po.status !== 'received' && po.status !== 'cancelled' ? (
                          <button
                            type="button"
                            onClick={() => handleMarkReceived(po)}
                            className="rounded-md bg-green-600 px-2 py-1 text-sm text-white"
                          >
                            Mark received
                          </button>
                        ) : null}
                        {(po.status === 'ordered' || po.status === 'received') && (
                          <button
                            type="button"
                            onClick={() => handleDownloadPo(po)}
                            className="rounded-md border border-blue-300 px-2 py-1 text-sm text-blue-700"
                          >
                            PO PDF
                          </button>
                        )}
                        {po.status === 'received' && !linkedInv && onNavigateToInvoices ? (
                          <button
                            type="button"
                            onClick={() => onNavigateToInvoices(po.id)}
                            className="rounded-md border border-gray-400 px-2 py-1 text-sm"
                          >
                            Record invoice
                          </button>
                        ) : null}
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {suppliers.map((s) => (
              <article key={s.id} className="rounded-xl border bg-white p-4 shadow-sm">
                <h3 className="font-semibold">{s.name}</h3>
                <p className="text-sm text-gray-600">{s.contactPerson}</p>
                <div className="mt-4 flex gap-2">
                  <button
                    type="button"
                    onClick={() => handleOpenBuilder(null, s.id)}
                    className="rounded-md bg-pink-600 px-3 py-1.5 text-sm text-white"
                  >
                    New PO
                  </button>
                  <button type="button" onClick={() => handleOpenSupplierModal(s)}>
                    <EditIcon className="h-5 w-5 text-pink-600" />
                  </button>
                  <button type="button" onClick={() => void onDeleteSupplier(s.id)}>
                    <TrashIcon className="h-5 w-5 text-red-600" />
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </main>

      {isSupplierModalOpen ? (
        <SupplierModal
          supplier={editingSupplier}
          onClose={() => setIsSupplierModalOpen(false)}
          onSave={(s) => {
            void onSaveSupplier(s);
            setIsSupplierModalOpen(false);
          }}
        />
      ) : null}

      {isBuilderOpen ? (
        <PurchaseOrderBuilder
          po={editingPO}
          suppliers={suppliers}
          inventoryItems={inventoryItems}
          businessInfo={businessInfo}
          purchasingDefaults={purchasingDefaults}
          lastOrderForSupplier={
            builderSupplierId != null ? lastOrderBySupplier.get(builderSupplierId) ?? null : null
          }
          initialSupplierId={builderSupplierId}
          onClose={() => {
            setIsBuilderOpen(false);
            setEditingPO(null);
            setBuilderSupplierId(undefined);
          }}
          onSave={async (po) => {
            await onSavePurchaseOrder(po);
            setIsBuilderOpen(false);
            setEditingPO(null);
            setBuilderSupplierId(undefined);
          }}
        />
      ) : null}

      {receiveTarget ? (
        <ReceivePreviewModal
          po={receiveTarget}
          inventory={inventoryItems}
          supplierName={supplierMap.get(receiveTarget.supplierId)?.name ?? ''}
          loading={receiveLoading}
          onCancel={() => setReceiveTarget(null)}
          onConfirm={confirmReceive}
        />
      ) : null}
    </div>
  );
};

export default PurchasingPage;
